import React, { useState } from "react";
import Modal from "../../../../shared/components/dashboard-layout/modalComponent";
import "./create-group-modal.scss";

const CreateGroupModal = ({ onClose, onCreateGroup, isLoading }) => {
  // Consolidated state
  const [formData, setFormData] = useState({
    groupName: "",
    groupDescription: "",
    isAnonymous: false,
    members: [{ firstName: "", lastName: "", phoneNumber: "" }]
  });
  const [errors, setErrors] = useState({});

  // Member management utilities
  const addMemberField = () => {
    setFormData(prev => ({
      ...prev,
      members: [...prev.members, { firstName: "", lastName: "", phoneNumber: "" }]
    }));
  };

  const removeMemberField = (index) => {
    if (formData.members.length > 1) {
      setFormData(prev => ({
        ...prev,
        members: prev.members.filter((_, i) => i !== index)
      }));
    }
  };

  const updateMember = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Form validation utility
  const validateForm = () => {
    const newErrors = {};
    const { groupName, members } = formData;

    if (!groupName.trim()) {
      newErrors.groupName = "Group name is required";
    }

    const validMembers = members.filter((member) => member.phoneNumber.trim());
    if (validMembers.length === 0) {
      newErrors.members = "At least one member with phone number is required";
    }

    // Validate phone numbers and names
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    const invalidMembers = validMembers.filter(
      (member) => !phoneRegex.test(member.phoneNumber.trim()) ||
                  !member.firstName.trim() ||
                  !member.lastName.trim()
    );
    if (invalidMembers.length > 0) {
      newErrors.memberFormat = "Please enter valid member details (first name, last name, and phone number)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Event handlers
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (validateForm()) {
      const validMembers = formData.members.filter((member) => member.phoneNumber.trim());
      onCreateGroup({
        name: formData.groupName.trim(),
        description: formData.groupDescription.trim(),
        isAnonymous: formData.isAnonymous,
        members: validMembers.map((member) => ({
          firstName: member.firstName.trim(),
          lastName: member.lastName.trim(),
          phone: member.phoneNumber.trim()
        })),
      });
    }
  };

  const handleSave = (e) => {
    if (isLoading) return;
    handleSubmit(e);
  };

  const renderGroupNameField = () => (
    <div className="form-group">
      <label htmlFor="groupName">Group Name *</label>
      <input
        type="text"
        id="groupName"
        value={formData.groupName}
        onChange={(e) => updateFormField("groupName", e.target.value)}
        placeholder="Enter group name"
        className={"form-control " + (errors.groupName ? "error" : "")}
      />
      {errors.groupName && (
        <span className="error-message">{errors.groupName}</span>
      )}
    </div>
  );

  const renderDescriptionField = () => (
    <div className="form-group">
      <label htmlFor="groupDescription">Description (Optional)</label>
      <textarea
        id="groupDescription"
        value={formData.groupDescription}
        onChange={(e) => updateFormField("groupDescription", e.target.value)}
        placeholder="Enter group description"
        className="form-control"
        rows="3"
      />
    </div>
  );

  const renderAnonymousField = () => (
    <div className="form-group">
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={formData.isAnonymous}
          onChange={(e) => updateFormField("isAnonymous", e.target.checked)}
          className="form-check-input"
        />
        <span className="checkmark"></span>
        Create as anonymous group
      </label>
      <div className="alert alert-info small">
        <i className="fas fa-info-circle me-2"></i>
        <span>Anonymous groups hide participant identities from each other</span>
      </div>
    </div>
  );

  const renderMemberField = (member, index) => (
    <div key={index} className="member-input-group align-items-center">
      <div className="member-fields">
        <input
          type="text"
          value={member.firstName}
          onChange={(e) => updateMember(index, "firstName", e.target.value)}
          placeholder="First name"
          className={"form-control " + (errors.memberFormat ? "error" : "")}
        />
        <input
          type="text"
          value={member.lastName}
          onChange={(e) => updateMember(index, "lastName", e.target.value)}
          placeholder="Last name"
          className={"form-control " + (errors.memberFormat ? "error" : "")}
        />
        <input
          type="tel"
          value={member.phoneNumber}
          onChange={(e) => updateMember(index, "phoneNumber", e.target.value)}
          placeholder="Phone (e.g., +1234567890)"
          className={"form-control " + (errors.members || errors.memberFormat ? "error" : "")}
        />
      </div>
      {formData.members.length > 1 && (
        <button
          type="button"
          className="icon-button red"
          onClick={() => removeMemberField(index)}
        >
          <i className="fas fa-trash"></i>
        </button>
      )}
    </div>
  );

  const renderMembersSection = () => (
    <div className="form-group">
      <div className="members-header">
        <label>Members *</label>
        <button
          type="button"
          className="bouncy-button small"
          onClick={addMemberField}
        >
          <i className="fas fa-plus"></i>
          <span>Add member</span>
        </button>
      </div>
      
      <div className="members-list">
        {formData.members.map((member, index) => renderMemberField(member, index))}
      </div>
      
      {errors.members && (
        <span className="error-message">{errors.members}</span>
      )}
      {errors.memberFormat && (
        <span className="error-message">{errors.memberFormat}</span>
      )}
      
      <div className="alert alert-info small">
        <i className="fas fa-info-circle me-2"></i>
        <span>Use E.164 format for phone numbers (use the country code, e.g., +14155552671)</span>
      </div>
    </div>
  );

  return (
    <Modal
      show={true}
      onClose={onClose}
      header={"Create New Group"}
      buttons={true}
      onSave={handleSave}
      saveLabel={isLoading ? "Creating..." : "Create Group"}
      saveDisabled={isLoading}
    >
      <div className="create-group-modal">
        <form onSubmit={handleSubmit} className="modal-form" style={{ padding: "0" }}>
          {renderGroupNameField()}
          {renderDescriptionField()}
          {renderMembersSection()}
          {renderAnonymousField()}
        </form>
      </div>
    </Modal>
  );
};

export default CreateGroupModal;
